import { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { ArrowLeft, Mail, Link2, Copy } from "lucide-react";

export default function CustomerLogin() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { requestMagicLink, verifyMagicLink } = useAuth();
  const [email, setEmail] = useState("");
  const [devLink, setDevLink] = useState("");
  const [busy, setBusy] = useState(false);
  const tokenParam = search.get("token");
  const verifiedRef = useRef(false);

  useEffect(() => {
    if (tokenParam && !verifiedRef.current) {
      verifiedRef.current = true;
      (async () => {
        try {
          await verifyMagicLink(tokenParam);
          toast.success("Signed in");
          navigate("/customer");
        } catch (err) { toast.error(formatApiError(err)); }
      })();
    }
  }, [tokenParam, verifyMagicLink, navigate]);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await requestMagicLink(email.trim());
      setDevLink(r.dev_link || "");
      toast.success("Magic link sent (mock)");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 md:p-12 grain relative">
      <div className="w-full max-w-md space-y-6">
        <Link to="/" className="font-mono text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2"><ArrowLeft className="w-3 h-3"/>Back</Link>
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-brand-green mb-2">// CUSTOMER ACCESS</div>
          <h2 className="text-4xl md:text-5xl font-display uppercase tracking-tighter">Email Sign-in</h2>
          <p className="text-sm text-muted-foreground mt-2">We'll email you a one-time sign-in link.</p>
        </div>
        <form onSubmit={send} className="space-y-4">
          <div className="space-y-2">
            <Label className="font-mono text-xs uppercase tracking-widest">Email</Label>
            <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"/>
              <Input required type="email" value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="you@company.com" className="h-14 pl-10 bg-white border-2" data-testid="customer-email-input"/></div>
          </div>
          <Button type="submit" disabled={busy} className="w-full h-14 bg-brand-green text-black hover:bg-brand-green/90 font-mono uppercase font-bold" data-testid="send-magic-link-btn">
            {busy ? "Sending..." : "Send Magic Link"}
          </Button>
        </form>
        {devLink && (
          <div className="border-2 border-dashed border-brand-green p-3 bg-brand-green/5" data-testid="dev-magic-link">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2"><Link2 className="w-3 h-3"/>MOCK EMAIL — click to sign in</div>
            <a href={devLink} className="font-mono text-xs text-brand-green break-all underline" data-testid="dev-magic-link-href">{devLink}</a>
            <Button size="sm" variant="outline" onClick={()=>navigator.clipboard.writeText(devLink)} className="mt-2 font-mono uppercase text-[10px]"><Copy className="w-3 h-3 mr-1"/>Copy</Button>
          </div>
        )}
      </div>
    </div>
  );
}
